import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { DevDocsBylineConnectionMenuSection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnectionMenuSection.ts';
import { expVal } from '@confluence/feature-experiments';

import { ConnectionsByLineExpandableLinkSection } from './ConnectionsByLineExpandableLinkSection.tsx';
import { ConnectionsByLineMenuFooter } from './footer/ConnectionsByLineMenuFooter.tsx';
import { ConnectionsByLineMenuHeader, HEADER_HEADING_ID } from './ConnectionsByLineMenuHeader.tsx';
import { ConnectionsByLineMenuItem } from './ConnectionsByLineMenuItem.tsx';
import {
	DEFAULT_VISIBLE_LINK_COUNT,
	getTopConnectionLinks,
} from './utils/getTopConnectionLinks.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const MENU_WIDTH = '360px';
const MENU_BODY_MAX_HEIGHT = '420px';
const TWG_MENU_BODY_MAX_HEIGHT = '600px';

const i18n = defineMessages({
	connected: {
		id: 'byline-connections.menu.section.connected',
		defaultMessage: 'Connected',
		description:
			'Status pill shown next to a product section heading after the user connects that product account.',
	},
	linksTo: {
		id: 'byline-connections.menu.section.links-to',
		defaultMessage: 'Links to ({count})',
		description:
			'Heading of the connections byline menu links section, followed by the total number of links found on the current page in parentheses.',
	},
	appearsIn: {
		id: 'byline-connections.menu.section.appears-in',
		defaultMessage: 'Appears in ({count})',
		description:
			'Heading of the connections byline menu incoming links section, followed by the total number of other pages and items where the current page appears in parentheses.',
	},
	viewAllLinks: {
		id: 'byline-connections.menu.section.view-all-links',
		defaultMessage: 'View all links',
		description:
			'Button below the truncated connections byline links list that expands the section to show every link found on the page.',
	},
	viewAllReferences: {
		id: 'byline-connections.menu.section.view-all-references',
		defaultMessage: 'View all references',
		description:
			'Button below the truncated connections byline references list that expands the section to show every page and item that references the current page.',
	},
});

export type ConnectionGroup = {
	product: string;
	productKey: string;
	items: EnhancedSmartLink[];
};

export type ConnectionItemHandler = (item: EnhancedSmartLink) => void;
export type RefreshLinkUrlHandler = (
	item: EnhancedSmartLink,
) => Promise<EnhancedSmartLink | undefined>;

const styles = cssMap({
	menu: {
		width: MENU_WIDTH,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
	content: {
		paddingBlockEnd: token('space.025'),
		maxHeight: MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	twgContent: {
		paddingBlockEnd: token('space.025'),
		maxHeight: TWG_MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	sectionHeading: {
		width: '100%',
	},
	connectedPill: {
		backgroundColor: token('color.background.success.subtler'),
		borderColor: token('color.border.accent.lime'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		color: token('color.text.accent.lime.bolder'),
		font: token('font.body.small'),
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
	},
});

type GroupHandlers = {
	refreshLinkUrl: RefreshLinkUrlHandler;
	recentlyConnectedProduct: string | null;
	onProductConnected: (product: string) => void;
	onItemHover?: ConnectionItemHandler;
	onNavigate?: () => void;
};

const ConnectedPill = () => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.connectedPill}>
			<Inline as="span" space="space.025" alignBlock="center">
				<CheckMarkIcon label="" size="small" color={token('color.icon.accent.lime')} />
				{formatMessage(i18n.connected)}
			</Inline>
		</Box>
	);
};

const ConnectionsByLineMenuSection = ({
	group,
	refreshLinkUrl,
	recentlyConnectedProduct,
	onProductConnected,
	onItemHover,
	onNavigate,
}: { group: ConnectionGroup } & GroupHandlers) => {
	const handleConnectSuccess = useCallback(() => {
		onProductConnected(group.product);
	}, [group.product, onProductConnected]);

	return (
		<Section>
			<HeadingItem>
				<Inline
					xcss={styles.sectionHeading}
					space="space.100"
					alignBlock="center"
					spread="space-between"
				>
					{group.product}
					{recentlyConnectedProduct === group.product ? <ConnectedPill /> : null}
				</Inline>
			</HeadingItem>
			{group.items.map((item) => (
				<ConnectionsByLineMenuItem
					key={item.ari}
					item={item}
					refreshLinkUrl={refreshLinkUrl}
					linkDirection="outgoing"
					onConnectSuccess={handleConnectSuccess}
					onHover={onItemHover}
					onNavigate={onNavigate}
				/>
			))}
		</Section>
	);
};

// Experiment-off treatment: one section per product group. Renders nothing when
// there are no groups.
const ConnectionsByLineGroupedSections = ({
	groups,
	...handlers
}: { groups: ConnectionGroup[] } & GroupHandlers) => {
	if (groups.length === 0) {
		return null;
	}

	return (
		<MenuGroup>
			{groups.map((group) => (
				<ConnectionsByLineMenuSection key={group.product} group={group} {...handlers} />
			))}
		</MenuGroup>
	);
};

type MenuShownAttributes = {
	linkCount: number;
	productBreakdown: Record<string, number>;
	authorizedLinkCount: number;
	unauthorizedLinkCount: number;
	connectableLinkCount: number;
};

const getMenuShownAttributes = (groups: ConnectionGroup[] = []): MenuShownAttributes => {
	const attrs: MenuShownAttributes = {
		linkCount: 0,
		productBreakdown: {},
		authorizedLinkCount: 0,
		unauthorizedLinkCount: 0,
		connectableLinkCount: 0,
	};

	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total link count; groups partition a finite link set
	for (const group of groups) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total link count; groups partition a finite link set
		for (const item of group.items) {
			attrs.linkCount += 1;
			attrs.productBreakdown[group.productKey] =
				(attrs.productBreakdown[group.productKey] ?? 0) + 1;
			const isAuthorized = item.access === 'granted';
			if (isAuthorized) {
				attrs.authorizedLinkCount += 1;
			} else {
				attrs.unauthorizedLinkCount += 1;
				if ((item.authProviders?.length ?? 0) > 0) {
					attrs.connectableLinkCount += 1;
				}
			}
		}
	}

	return attrs;
};

const getUnauthenticatedLinkCount = (links: EnhancedSmartLink[] = []): number =>
	links.filter((link) => link.access === 'unauthorized').length;

export type ConnectionsByLineMenuProps = {
	showFooter?: boolean;
	isOpen?: boolean;
	groups?: ConnectionGroup[];
	links?: EnhancedSmartLink[];
	incomingLinks?: EnhancedSmartLink[];
	devDocsConnection?: DevDocsBylineConnection | null;
	contentId?: string;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onItemHover?: ConnectionItemHandler;
	onNavigate?: () => void;
};

export const ConnectionsByLineMenu = ({
	showFooter = true,
	isOpen = true,
	groups,
	links,
	incomingLinks,
	devDocsConnection,
	contentId,
	refreshLinkUrl,
	onItemHover,
	onNavigate,
}: ConnectionsByLineMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const menuShownAttributes = useMemo(() => getMenuShownAttributes(groups), [groups]);
	const unauthenticatedLinkCount = useMemo(() => getUnauthenticatedLinkCount(links), [links]);
	const hasFiredRef = useRef(false);
	const hasFiredUnauthenticatedRef = useRef(false);
	const [recentlyConnectedProduct, setRecentlyConnectedProduct] = useState<string | null>(null);
	const [isLinksExpanded, setIsLinksExpanded] = useState(false);
	const [isReferencedInExpanded, setIsReferencedInExpanded] = useState(false);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'menu',
				actionSubjectId: 'connectionsBylineMenu',
				source: 'connectionsBylineMenu',
				attributes: menuShownAttributes,
			},
		}).fire();
	}, [createAnalyticsEvent, menuShownAttributes]);

	useEffect(() => {
		if (hasFiredUnauthenticatedRef.current || unauthenticatedLinkCount === 0) {
			return;
		}
		hasFiredUnauthenticatedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'unauthenticatedSmartlink',
				actionSubjectId: 'connectionsBylineMenu',
				source: 'connectionsBylineMenu',
				attributes: { unauthenticatedLinkCount },
			},
		}).fire();
	}, [createAnalyticsEvent, unauthenticatedLinkCount]);

	useEffect(() => {
		if (!isOpen) {
			setRecentlyConnectedProduct(null);
			setIsLinksExpanded(false);
			setIsReferencedInExpanded(false);
		}
	}, [isOpen]);

	const handleProductConnected = useCallback((product: string) => {
		setRecentlyConnectedProduct(product);
	}, []);

	// When the experiment is on, the menu drops product grouping and shows a flat
	// list of the top links (unconnected first, then document order), expandable to
	// the full list via a "View all links" button.
	const totalLinkCount = links?.length ?? 0;
	const visibleLinks = useMemo(
		() =>
			getTopConnectionLinks(
				links ?? [],
				isLinksExpanded ? totalLinkCount : DEFAULT_VISIBLE_LINK_COUNT,
			),
		[links, isLinksExpanded, totalLinkCount],
	);
	const canExpandLinks = !isLinksExpanded && totalLinkCount > DEFAULT_VISIBLE_LINK_COUNT;

	const handleExpandLinks = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineViewAllLinks',
				source: 'connectionsBylineMenu',
				attributes: { linkCount: totalLinkCount },
			},
		}).fire();
		setIsLinksExpanded(true);
	}, [createAnalyticsEvent, totalLinkCount]);

	// 1P references keep the query's recency order (no unconnected-first reordering
	// like "Links to", since these have no "Connect" action).
	const totalReferencedInCount = incomingLinks?.length ?? 0;
	const visibleReferencedIn = useMemo(
		() =>
			(incomingLinks ?? []).slice(
				0,
				isReferencedInExpanded ? totalReferencedInCount : DEFAULT_VISIBLE_LINK_COUNT,
			),
		[incomingLinks, isReferencedInExpanded, totalReferencedInCount],
	);
	const canExpandReferencedIn =
		!isReferencedInExpanded && totalReferencedInCount > DEFAULT_VISIBLE_LINK_COUNT;

	const handleExpandReferencedIn = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineViewAllReferences',
				source: 'connectionsBylineMenu',
				attributes: { linkCount: totalReferencedInCount },
			},
		}).fire();
		setIsReferencedInExpanded(true);
	}, [createAnalyticsEvent, totalReferencedInCount]);

	return (
		<Box xcss={styles.menu} role="dialog" aria-labelledby={HEADER_HEADING_ID}>
			<Stack>
				<ConnectionsByLineMenuHeader />
				<Box
					xcss={
						expVal('1p_3p_connections_byline_twg', 'isEnabled', false)
							? styles.twgContent
							: styles.content
					}
				>
					{devDocsConnection ? (
						<MenuGroup>
							<DevDocsBylineConnectionMenuSection
								connection={devDocsConnection}
								onNavigate={onNavigate}
							/>
						</MenuGroup>
					) : null}
					{expVal('1p_3p_connections_byline_twg', 'isEnabled', false) ? (
						<>
							<ConnectionsByLineExpandableLinkSection
								heading={formatMessage(i18n.appearsIn, { count: totalReferencedInCount })}
								links={visibleReferencedIn}
								shouldShowExpandButton={canExpandReferencedIn}
								expandButtonLabel={formatMessage(i18n.viewAllReferences)}
								onExpand={handleExpandReferencedIn}
								refreshLinkUrl={refreshLinkUrl}
								linkDirection="incoming"
								onItemHover={onItemHover}
								onNavigate={onNavigate}
							/>
							<ConnectionsByLineExpandableLinkSection
								heading={formatMessage(i18n.linksTo, { count: totalLinkCount })}
								links={visibleLinks}
								shouldShowExpandButton={canExpandLinks}
								expandButtonLabel={formatMessage(i18n.viewAllLinks)}
								onExpand={handleExpandLinks}
								refreshLinkUrl={refreshLinkUrl}
								linkDirection="outgoing"
								onItemHover={onItemHover}
								onNavigate={onNavigate}
							/>
						</>
					) : (
						<ConnectionsByLineGroupedSections
							groups={groups ?? []}
							refreshLinkUrl={refreshLinkUrl}
							recentlyConnectedProduct={recentlyConnectedProduct}
							onProductConnected={handleProductConnected}
							onItemHover={onItemHover}
							onNavigate={onNavigate}
						/>
					)}
				</Box>
				{showFooter ? (
					<ConnectionsByLineMenuFooter links={links ?? []} contentId={contentId} />
				) : null}
			</Stack>
		</Box>
	);
};
